import { RingProgress } from '@mantine/core';
import {
  IconWorld,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
} from '@tabler/icons-react';
import { Box, Card, Group, Progress, SimpleGrid, Stack, Table, Text, Title } from '@/components/ui';
import { trafficSources, deviceBreakdown, geoData } from '../mockData/analytics';

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US').format(value);
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const getDeviceIcon = (device: string) => {
  switch (device.toLowerCase()) {
    case 'desktop': return IconDeviceDesktop;
    case 'mobile': return IconDeviceMobile;
    case 'tablet': return IconDeviceTablet;
    default: return IconDeviceDesktop;
  }
};

export default function AudiencePage() {
  const totalVisitors = trafficSources.reduce((acc, s) => acc + s.visitors, 0);
  const totalSessions = deviceBreakdown.reduce((acc, d) => acc + d.sessions, 0);
  const totalGeoVisitors = geoData.reduce((acc, g) => acc + g.visitors, 0);
  const totalGeoRevenue = geoData.reduce((acc, g) => acc + g.revenue, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Box>
        <Title order={2}>Audience Overview</Title>
        <Text c="dimmed" size="sm">Understand who visits your website</Text>
      </Box>

      {/* Traffic Sources and Device Breakdown */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Traffic Sources */}
        <Card>
          <Text fw={600} mb="md">Traffic Sources</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={trafficSources.map(s => ({
                value: s.percentage,
                color: s.color,
              }))}
              label={
                <Box ta="center">
                  <Text size="xl" fw={700}>{formatNumber(totalVisitors)}</Text>
                  <Text size="xs" c="dimmed">Visitors</Text>
                </Box>
              }
            />
            <Stack gap="xs" style={{ flex: 1 }}>
              {trafficSources.map(source => (
                <Group key={source.source} justify="space-between">
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${source.color}-filled)` }}
                    />
                    <Text size="sm">{source.source}</Text>
                  </Group>
                  <Text size="sm" fw={500}>{source.percentage}%</Text>
                </Group>
              ))}
            </Stack>
          </Group>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <Text fw={600} mb="md">Device Breakdown</Text>
          <Stack gap="md">
            {deviceBreakdown.map(device => {
              const Icon = getDeviceIcon(device.device);
              return (
                <Box key={device.device} p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                  <Group justify="space-between" mb="xs">
                    <Group gap="sm">
                      <Box
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          backgroundColor: `var(--mantine-color-${device.color}-light)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={20} color={`var(--mantine-color-${device.color}-filled)`} />
                      </Box>
                      <Box>
                        <Text size="sm" fw={500}>{device.device}</Text>
                        <Text size="xs" c="dimmed">{formatNumber(device.sessions)} sessions</Text>
                      </Box>
                    </Group>
                    <Text size="lg" fw={700}>{device.percentage}%</Text>
                  </Group>
                  <Progress
                    value={device.percentage}
                    size="sm"
                    radius="xl"
                    color={device.color}
                  />
                </Box>
              );
            })}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Geographic Data */}
      <Card>
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <IconWorld size={20} />
            <Text fw={600}>Geographic Distribution</Text>
          </Group>
          <Group gap="xl">
            <Box>
              <Text size="xs" c="dimmed">Total Visitors</Text>
              <Text fw={600}>{formatNumber(totalGeoVisitors)}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Total Revenue</Text>
              <Text fw={600}>{formatCurrency(totalGeoRevenue)}</Text>
            </Box>
          </Group>
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Country</Table.Th>
              <Table.Th>Visitors</Table.Th>
              <Table.Th>% of Total</Table.Th>
              <Table.Th ta="right">Revenue</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {geoData.map(geo => {
              const percentage = ((geo.visitors / totalGeoVisitors) * 100).toFixed(1);
              return (
                <Table.Tr key={geo.country}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{geo.country}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatNumber(geo.visitors)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="sm">
                      <Progress
                        value={parseFloat(percentage)}
                        size="sm"
                        radius="xl"
                        w={100}
                        color="blue"
                      />
                      <Text size="sm" c="dimmed">{percentage}%</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>{formatCurrency(geo.revenue)}</Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Traffic Source Details */}
      <Card>
        <Text fw={600} mb="md">Traffic Source Details</Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Source</Table.Th>
              <Table.Th>Visitors</Table.Th>
              <Table.Th>% of Total</Table.Th>
              <Table.Th>Distribution</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {trafficSources.map(source => (
              <Table.Tr key={source.source}>
                <Table.Td>
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${source.color}-filled)` }}
                    />
                    <Text size="sm" fw={500}>{source.source}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatNumber(source.visitors)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{source.percentage}%</Text>
                </Table.Td>
                <Table.Td>
                  <Progress
                    value={source.percentage}
                    size="lg"
                    radius="xl"
                    color={source.color}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}
